import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { queryClient } from "../api/queryClient";
import type {
  LoginContext,
  PendingContextSelection,
  SwitchContextPayload,
} from "../api/services/authService";
import { authService } from "../api/services/authService";

// Re-export multi-context types so consumers import from one place
export type { LoginContext, PendingContextSelection, SwitchContextPayload };

interface User {
  id: string;
  first_name?: string;
  last_name?: string;
  name: string | undefined;
  email: string;
  role: string;
  scope: "platform" | "tenant" | "org";
  scopeId: string;
  agencyId?: string;
  organizationId?: string;
  parent_agency_name?: string;
  parent_org_name?: string;
  status: string;
  mustChangePassword?: boolean;
}

interface AuthState {
  user: User | null;
  permissions: string[];
  isLoading: boolean;
  isAuthenticated: boolean;
  mustChangePassword: boolean;

  // Multi-context state
  pendingContextSelection: PendingContextSelection | null;
  availableContexts: LoginContext[] | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>;
  setUser: (user: User | null) => void;
  setPermissions: (permissions: string[]) => void;
  setLoading: (isLoading: boolean) => void;

  // Multi-context actions
  setPendingContextSelection: (data: PendingContextSelection | null) => void;
  setAvailableContexts: (contexts: LoginContext[] | null) => void;
  selectContext: (payload: {
    selection_token: string;
    selected_context_type: "PLATFORM" | "TENANT";
    selected_scope_id: string | null;
  }) => Promise<void>;
  switchContext: (payload: SwitchContextPayload) => Promise<void>;

  // Permission checks
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      permissions: [],
      isLoading: true,
      isAuthenticated: false,
      mustChangePassword: false,
      pendingContextSelection: null,
      availableContexts: null,

      login: async (email: string, password: string) => {
        const result = await authService.login(email, password);

        if (result.type === "select_context") {
          set(
            { pendingContextSelection: result.data, isLoading: false },
            false,
            "auth/login/selectContext",
          );
          return;
        }

        // Invalidate all queries to force fresh data fetch after login
        queryClient.invalidateQueries();
        set(
          {
            user: {
              ...result.user,
              mustChangePassword: result.mustChangePassword,
            },
            permissions: result.permissions,
            isAuthenticated: true,
            mustChangePassword: result.mustChangePassword || false,
          },
          false,
          "auth/login",
        );
      },

      logout: async () => {
        try {
          const resp = await authService.logout();
          if (import.meta.env.DEV) {
            console.log("Logout response:", resp?.message || resp);
          }
        } catch (error) {
          console.error("Logout failed:", error);
        }

        // Clear React Query cache to prevent stale data on next login
        queryClient.clear();
        set(
          {
            user: null,
            permissions: [],
            isAuthenticated: false,
            mustChangePassword: false,
          },
          false,
          "auth/logout",
        );
      },

      loadUser: async () => {
        set({ isLoading: true }, false, "auth/loadUser/start");
        try {
          const data = await authService.me();
          // The /me response may include mustChangePassword either at the root
          // or nested under user depending on backend. Normalize safely.
          const mustChange =
            (data as any).mustChangePassword ??
            (data.user as any)?.mustChangePassword ??
            false;

          set(
            {
              user: data.user,
              permissions: data.permissions,
              isAuthenticated: true,
              isLoading: false,
              mustChangePassword: mustChange,
            },
            false,
            "auth/loadUser/success",
          );
        } catch (error) {
          set(
            {
              user: null,
              permissions: [],
              isAuthenticated: false,
              isLoading: false,
              mustChangePassword: false,
            },
            false,
            "auth/loadUser/error",
          );
        }
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        await authService.changePassword(currentPassword, newPassword);
        // After successful password change, clear the mustChangePassword flag
        const currentUser = get().user;
        if (currentUser) {
          set(
            {
              user: { ...currentUser, mustChangePassword: false },
              mustChangePassword: false,
            },
            false,
            "auth/changePassword",
          );
        }
      },

      setUser: (user) =>
        set({ user, isAuthenticated: !!user }, false, "auth/setUser"),
      setPermissions: (permissions) =>
        set({ permissions }, false, "auth/setPermissions"),
      setLoading: (isLoading) => set({ isLoading }, false, "auth/setLoading"),

      // Multi-context actions
      setPendingContextSelection: (data) =>
        set(
          { pendingContextSelection: data },
          false,
          "auth/setPendingContextSelection",
        ),
      setAvailableContexts: (contexts) =>
        set(
          { availableContexts: contexts },
          false,
          "auth/setAvailableContexts",
        ),

      selectContext: async (payload) => {
        set({ isLoading: true }, false, "auth/selectContext/start");
        try {
          const result = await authService.selectContext(payload);
          queryClient.invalidateQueries();
          set(
            {
              user: {
                ...result.user,
                mustChangePassword: result.mustChangePassword,
              },
              permissions: result.permissions,
              isAuthenticated: true,
              mustChangePassword: result.mustChangePassword || false,
              pendingContextSelection: null,
              isLoading: false,
            },
            false,
            "auth/selectContext/success",
          );
        } catch (err) {
          set({ isLoading: false }, false, "auth/selectContext/error");
          throw err;
        }
      },

      switchContext: async (payload) => {
        set({ isLoading: true }, false, "auth/switchContext/start");
        try {
          const result = await authService.switchContext(payload);
          queryClient.clear();
          set(
            {
              user: {
                ...result.user,
                mustChangePassword: result.mustChangePassword,
              },
              permissions: result.permissions,
              isAuthenticated: true,
              mustChangePassword: result.mustChangePassword || false,
              isLoading: false,
            },
            false,
            "auth/switchContext/success",
          );
        } catch (err) {
          set({ isLoading: false }, false, "auth/switchContext/error");
          throw err;
        }
      },

      hasPermission: (permission: string) => {
        const { user, permissions } = get();
        if (!user) return false;
        return permissions.includes(permission);
      },

      hasAnyPermission: (perms: string[]) => {
        const { user, permissions } = get();
        if (!user) return false;
        return perms.some((perm) => permissions.includes(perm));
      },

      hasAllPermissions: (perms: string[]) => {
        const { user, permissions } = get();
        if (!user) return false;
        return perms.every((perm) => permissions.includes(perm));
      },
    }),
    { name: "AuthStore" },
  ),
);
